import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, total, count } = useCart();
  const [usConfirmed, setUsConfirmed] = useState(false);

  const usOnlyItems = items.filter((i) =>
    (i.shipping || "").startsWith("Ships to:")
  );
  const checkoutBlocked = usOnlyItems.length > 0 && !usConfirmed;

  useEffect(() => {
    if (usOnlyItems.length === 0) setUsConfirmed(false);
  }, [usOnlyItems.length]);

  const handleCheckout = async () => {
    if (checkoutBlocked) {
      toast.error(
        "Some items ship to the United States only — confirm your address first."
      );
      return;
    }
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        lines: items.map((i) => ({ variantId: i.variantId, qty: i.qty })),
      });
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.info("Checkout activates once the Shopify connection is switched on.");
      }
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="flex w-full flex-col border-line bg-bone p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-line px-6 py-6">
          <SheetTitle className="font-serif text-3xl font-medium tracking-tight text-ink">
            Your Bag
          </SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-[0.2em] text-smoke">
            {count === 0 ? "Nothing here yet" : `${count} item${count > 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <p
              data-testid="cart-empty-message"
              className="mt-10 text-sm leading-relaxed text-smoke"
            >
              Your bag is empty. Wander through the catalogue and pick an object
              for a better mood.
            </p>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-4"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden bg-white">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex flex-1 flex-col justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-serif text-lg font-medium leading-tight text-ink">
                          {item.name}
                        </p>
                        <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-smoke">
                          {item.tag}
                        </p>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.id}`}
                        onClick={() => removeItem(item.id)}
                        className="text-smoke transition-colors duration-300 hover:text-ink"
                        aria-label={`Remove ${item.name}`}
                      >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-line">
                        <button
                          data-testid={`cart-qty-decrease-${item.id}`}
                          onClick={() => setQty(item.id, item.qty - 1)}
                          className="px-3 py-1.5 text-smoke transition-colors duration-300 hover:text-ink"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                        <span
                          data-testid={`cart-qty-${item.id}`}
                          className="min-w-8 text-center text-sm text-ink"
                        >
                          {item.qty}
                        </span>
                        <button
                          data-testid={`cart-qty-increase-${item.id}`}
                          onClick={() => setQty(item.id, item.qty + 1)}
                          className="px-3 py-1.5 text-smoke transition-colors duration-300 hover:text-ink"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <span className="text-sm font-medium text-ink">
                        {fmt(item.price * item.qty)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-line px-6 py-6">
            {usOnlyItems.length > 0 && (
              <div
                data-testid="us-only-warning"
                className="mb-5 border border-ember bg-ember/10 p-4"
              >
                <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-ink">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-ember" strokeWidth={1.5} />
                  Ships to the United States only
                </p>
                <p className="mt-2 text-xs leading-relaxed text-smoke">
                  {usOnlyItems.map((i) => i.name).join(", ")} can only be
                  delivered to a US address. US-only items are non-refundable.
                </p>
                <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={usConfirmed}
                    onChange={(e) => setUsConfirmed(e.target.checked)}
                    data-testid="us-confirm-checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#D2968C]"
                  />
                  <span className="text-xs leading-relaxed text-ink">
                    My delivery address is in the United States
                  </span>
                </label>
              </div>
            )}
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-smoke">
                Subtotal
              </span>
              <span data-testid="cart-total" className="font-serif text-2xl font-medium text-ink">
                {fmt(total)}
              </span>
            </div>
            <p className="mb-6 text-[11px] text-smoke">
              Shipping calculated at checkout.
            </p>
            <button
              data-testid="checkout-button"
              onClick={handleCheckout}
              disabled={checkoutBlocked}
              className={`w-full py-4 text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
                checkoutBlocked
                  ? "cursor-not-allowed bg-line text-smoke"
                  : "bg-ink text-bone hover:bg-ember hover:text-ink"
              }`}
            >
              {checkoutBlocked ? "Confirm US Address First" : "Checkout"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
